import React, { useState, useEffect } from 'react';
import AIInterviewCoach from './AIInterviewCoach';
import AssistantLogo from './AssistantLogo';
import { useInterview } from './useInterviewContext';
import './GlobalCoach.css';

const GlobalCoach = () => {
    const {
        isCoachOpen,
        toggleCoach,
        userContext,
        updateUserContext,
        clearHistory,
        setActiveTopic,
        coachMode,
        setCoachMode,
        voiceSettings,
        updateVoiceSettings,
        speakText
    } = useInterview();
    const [showSettings, setShowSettings] = useState(false);
    const [voices, setVoices] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);

    // Auto-show settings if opened on dedicated page
    useEffect(() => {
        if (isCoachOpen && window.location.hash === '#assistant') {
            setShowSettings(true);
        }
    }, [isCoachOpen]);


    // Detect Page Context based on URL hash
    useEffect(() => {
        const loadVoices = () => {
            const allVoices = window.speechSynthesis.getVoices();
            if (allVoices.length === 0) return;

            // Filter for only English and prioritize high quality/common ones
            const topVoices = allVoices.filter(v => {
                const isEnglish = v.lang.startsWith('en');
                const isHighQuality = v.name.includes('Google') ||
                    v.name.includes('Neural') ||
                    v.name.includes('Premium') ||
                    v.name.includes('Samantha') ||
                    v.name.includes('Daniel') ||
                    v.name.includes('Karen') ||
                    v.name.includes('Alex');
                return isEnglish && isHighQuality;
            }).slice(0, 10); // Limit to top 10

            if (topVoices.length > 0) {
                setVoices(topVoices);
            } else {
                // Fallback to first 5 English if no specific favorites found
                setVoices(allVoices.filter(v => v.lang.startsWith('en')).slice(0, 5));
            }
        };

        loadVoices();
        if (window.speechSynthesis.onvoiceschanged !== undefined) {
            window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
        }

        return () => {
            window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
        };
    }, []);

    const handleRouteContext = () => {
        const hash = window.location.hash.replace('#', '');
        let topic = null;

        if (hash === 'java-interview' || hash === 'java-deep-dive' || hash === 'springboot' || ['pdm', 'tpm', 'em', 'swe'].includes(hash)) {
            let topicTitle = "Technical Interview";
            if (hash === 'java-interview') topicTitle = "Java Full Stack";
            if (hash === 'java-deep-dive') topicTitle = "Java & OOPs Deep Dive";
            if (hash === 'springboot') topicTitle = "Spring Boot Deep Dive";

            topic = { title: topicTitle, type: hash.includes('java') || hash === 'springboot' ? 'swe' : hash };

            if (coachMode === 'salesman') {
                setCoachMode('interview');
                clearHistory();
            } else {
                setCoachMode('interview');
            }
        } else if (!hash || hash === 'home' || hash === 'nordstrom-timeline') {
            // Home/Landing Page/Nordstrom Timeline - Force clean slate for Saara (Salesman/Assistant Persona)
            topic = null;
            if (coachMode !== 'salesman') {
                setCoachMode('salesman');
                clearHistory();
            }
        } else if (hash === 'assistant') {
            // Dedicated Assistant Page - Force Salesman/Assistant Persona
            topic = null;
            if (coachMode !== 'salesman') {
                setCoachMode('salesman');
            }
        }

        setActiveTopic(topic);
    };

    useEffect(() => {
        window.addEventListener('hashchange', handleRouteContext);
        handleRouteContext();
        return () => window.removeEventListener('hashchange', handleRouteContext);
    }, [setActiveTopic]);

    // Don't show the floating FAB or the sidebar if we are on the dedicated assistant page
    const isDedicatedPage = window.location.hash === '#assistant';
    const assistantName = "Saara | सार";

    // On dedicated page, we only care about the settings overlay, 
    // which is usually triggered by a gear icon we'll add to AssistantPage.
    // However, to keep it simple, let's allow the panel to open BUT hide the internal AI Coach 
    // since AssistantPage already has one.


    return (
        <div className={`global-coach-wrapper ${isCoachOpen ? 'open' : ''} ${isExpanded ? 'expanded' : ''}`}>
            {/* Floating Action Button */}
            {!isCoachOpen && !isDedicatedPage && (
                <button
                    className={`coach-fab ${(coachMode === 'salesman' || isDedicatedPage) ? 'sales' : ''} ${isDedicatedPage ? 'compact' : ''}`}
                    onClick={() => toggleCoach(true)}
                    title={assistantName}
                >
                    <span className="fab-icon">
                        <AssistantLogo size="small" />
                    </span>
                    {!isDedicatedPage && (
                        <span className="fab-text">{assistantName}</span>
                    )}
                </button>
            )}

            {/* Coach Sidebar Panel */}
            <div className="coach-panel">
                <div className="coach-panel-header">
                    <div className="header-main">
                        <div className="header-title-group">
                            {(coachMode === 'salesman' || isDedicatedPage) && <AssistantLogo size="small" />}
                            <h3>{(coachMode === 'salesman' || isDedicatedPage) ? assistantName : '🤖 AI Interview Coach'}</h3>
                        </div>
                        <div className="header-actions">
                            {coachMode !== 'salesman' && (
                                <button
                                    className={`mode-toggle-btn ${coachMode}`}
                                    onClick={() => setCoachMode(coachMode === 'interview' ? 'learn' : 'interview')}
                                    title={`Switch to ${coachMode === 'interview' ? 'Learn' : 'Interview'} Mode`}
                                >
                                    {coachMode === 'interview' ? '🎯 Interview' : '📖 Learn'}
                                </button>
                            )}
                            <button
                                className="icon-btn"
                                onClick={() => setIsExpanded(!isExpanded)}
                                title={isExpanded ? "Minimize" : "Expand to Popup"}
                            >
                                {isExpanded ? "❐" : "⬜"}
                            </button>
                            <button className="icon-btn" onClick={() => setShowSettings(!showSettings)} title="Settings">⚙️</button>
                            <button className="icon-btn" onClick={() => toggleCoach(false)} title="Close">✕</button>
                        </div>
                    </div>
                </div>

                {showSettings && (
                    <div className="coach-settings-overlay premium-settings">
                        <div className="settings-header">
                            <h4>Configuration</h4>
                            <button className="close-settings" onClick={() => {
                                if (isDedicatedPage) {
                                    setShowSettings(false);
                                } else {
                                    setShowSettings(false);
                                }
                            }}>✕</button>
                        </div>

                        <div className="settings-grid">
                            {/* CARD 1: Career Profile */}
                            <div className="settings-card">
                                <div className="card-label">
                                    <span>💼 Career Context</span>
                                </div>
                                <div className="settings-group">
                                    <label>Target Role</label>
                                    <input
                                        type="text"
                                        value={userContext.role}
                                        onChange={(e) => updateUserContext({ role: e.target.value })}
                                        placeholder="e.g. Senior Java Developer"
                                    />
                                </div>
                                <div className="settings-group">
                                    <label>Target Company</label>
                                    <input
                                        type="text"
                                        value={userContext.company}
                                        onChange={(e) => updateUserContext({ company: e.target.value })}
                                        placeholder="e.g. Google, Amex"
                                    />
                                </div>
                            </div>

                            {/* CARD 2: Voice Dynamics */}
                            <div className="settings-card">
                                <div className="card-label">
                                    <span>🔊 Voice Personality</span>
                                </div>
                                <div className="settings-group">
                                    <label>Voice Engine</label>
                                    <div className="select-with-test">
                                        <select
                                            value={voiceSettings.voiceUri}
                                            onChange={(e) => updateVoiceSettings({ voiceUri: e.target.value })}
                                        >
                                            <option value="">Default System Voice</option>
                                            {voices.map(v => (
                                                <option key={v.voiceURI} value={v.voiceURI}>
                                                    {v.name}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            className="test-voice-btn-mini"
                                            onClick={() => speakText("Testing Guru's Assistant voice.", true)}
                                            title="Test Voice"
                                        >
                                            ▶
                                        </button>
                                    </div>
                                </div>
                                <div className="range-controls">
                                    <div className="range-item">
                                        <div className="range-info">
                                            <label>Speed</label>
                                            <span className="range-value">{voiceSettings.rate}x</span>
                                        </div>
                                        <input
                                            type="range" min="0.5" max="2" step="0.1"
                                            value={voiceSettings.rate}
                                            onChange={(e) => updateVoiceSettings({ rate: parseFloat(e.target.value) })}
                                        />
                                    </div>
                                    <div className="range-item">
                                        <div className="range-info">
                                            <label>Pitch</label>
                                            <span className="range-value">{voiceSettings.pitch}</span>
                                        </div>
                                        <input
                                            type="range" min="0.5" max="2" step="0.1"
                                            value={voiceSettings.pitch}
                                            onChange={(e) => updateVoiceSettings({ pitch: parseFloat(e.target.value) })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* CARD 3: Neural Engine */}
                            <div className={`settings-card neural-card ${voiceSettings.useNeuralVoice ? 'active' : ''}`}>
                                <div className="card-label">
                                    <span>🧠 Neural Processing</span>
                                    {voiceSettings.useNeuralVoice && voiceSettings.elevenLabsKey && (
                                        <span className="neural-status-dot"></span>
                                    )}
                                </div>
                                <label className="premium-toggle">
                                    <input
                                        type="checkbox"
                                        checked={voiceSettings.useNeuralVoice}
                                        onChange={(e) => updateVoiceSettings({ useNeuralVoice: e.target.checked })}
                                    />
                                    <span className="toggle-text">Enable ElevenLabs High-Fidelity</span>
                                </label>

                                {voiceSettings.useNeuralVoice && voiceSettings.premiumVoices && (
                                    <div className="premium-subset">
                                        <label>Select Persona</label>
                                        <select
                                            className="premium-voice-select"
                                            value={voiceSettings.elevenLabsVoiceId}
                                            onChange={(e) => updateVoiceSettings({ elevenLabsVoiceId: e.target.value })}
                                        >
                                            {voiceSettings.premiumVoices.map(pv => (
                                                <option key={pv.id} value={pv.id}>{pv.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* CARD 4: Data Management */}
                            <div className="settings-card danger-card">
                                <div className="card-label">
                                    <span>🧹 Data Protection</span>
                                </div>
                                <p className="settings-hint">Resetting will permanently clear your conversation context.</p>
                                <button className="clear-btn-full" onClick={() => { if (window.confirm('Clear chat history?')) clearHistory(); }}>
                                    Purge Session History
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="coach-panel-content">
                    {/* Don't render internal coach on dedicated page to avoid duplication */}
                    {!isDedicatedPage && <AIInterviewCoach />}
                </div>
            </div>
        </div>
    );
};

export default GlobalCoach;
